import { ArrowDown, ArrowUp, ImagePlus, Loader2, Plus, Trash2, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";

export const resolveImg = (base, u) => {
  if (!u) return "";
  if (/^https?:\/\//.test(u)) return u;
  if (u.startsWith("/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return base ? `${base.replace(/\/+$/, "")}/${u}` : "";
};

const uploadOne = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await api.post("/admin/upload", fd)).data.url;
};

export const Text = ({ label, value, onChange, testid, rows }) => (
  <label className="field">
    <span className="field-label">{label}</span>
    {rows ? (
      <textarea rows={rows} className="field-input !h-auto py-3" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    ) : (
      <input className="field-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    )}
  </label>
);

export const Lines = ({ label, value = [], onChange, testid }) => (
  <Text label={`${label} (one per line)`} rows={4} value={value.join("\n")} testid={testid}
    onChange={(t) => onChange(t.split("\n").map((x) => x.trimStart()).filter((x, i, a) => x || i === a.length - 1))} />
);

const useUpload = (onDone) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const run = async (files) => {
    setBusy(true);
    const urls = [];
    try {
      for (const f of files) urls.push(await uploadOne(f));
      toast.success(urls.length > 1 ? `${urls.length} photos uploaded` : "Photo uploaded");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      if (urls.length) onDone(urls);
      setBusy(false);
      ref.current.value = "";
    }
  };
  return { ref, busy, run };
};

export const Photo = ({ wid, label, value, onChange, testid }) => {
  const up = useUpload((u) => onChange(u[0]));
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <div className="flex gap-3">
        <div className="h-20 w-28 shrink-0 overflow-hidden rounded-xl border border-ink/10 bg-linen">
          {value && <img src={resolveImg(wid, value)} alt="" className="h-full w-full object-cover" data-testid={`${testid}-preview`} />}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <input className="field-input !h-10" value={value || ""} placeholder="Image URL" onChange={(e) => onChange(e.target.value)} data-testid={testid} />
          <div>
            <input ref={up.ref} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && up.run([e.target.files[0]])} data-testid={`${testid}-file`} />
            <button type="button" className="btn-ghost" disabled={up.busy} onClick={() => up.ref.current.click()} data-testid={`${testid}-upload`}>
              {up.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}Upload photo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const move = (arr, i, d) => {
  const a = [...arr];
  [a[i], a[i + d]] = [a[i + d], a[i]];
  return a;
};

export const Photos = ({ wid: base, label, value = [], onChange, testid, max = 40 }) => {
  const up = useUpload((u) => onChange([...value, ...u].slice(0, max)));
  return (
    <div className="field" data-testid={testid}>
      <span className="field-label">{label} ({value.length}/{max})</span>
      <div className="flex flex-wrap gap-3">
        {value.map((u, i) => (
          <div key={`${u}-${i}`} className="group relative h-24 w-32 overflow-hidden rounded-xl border border-ink/10" data-testid={`${testid}-item-${i}`}>
            {resolveImg(base, u) ? <img src={resolveImg(base, u)} alt="" className="h-full w-full object-cover" />
              : <span className="flex h-full items-center justify-center break-all bg-linen p-2 text-center text-[10px] text-ink/55">{u}<br />(set Website address to preview)</span>}
            <div className="absolute inset-x-1 top-1 flex justify-between">
              <span className="flex gap-1">
                {i > 0 && <button type="button" aria-label="Move left" onClick={() => onChange(move(value, i, -1))} className="grid h-6 w-6 place-items-center rounded-full bg-white/90"><ArrowUp className="h-3 w-3 -rotate-90" /></button>}
                {i < value.length - 1 && <button type="button" aria-label="Move right" onClick={() => onChange(move(value, i, 1))} className="grid h-6 w-6 place-items-center rounded-full bg-white/90"><ArrowDown className="h-3 w-3 -rotate-90" /></button>}
              </span>
              <button type="button" aria-label="Remove photo" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}
                className="grid h-6 w-6 place-items-center rounded-full bg-white/90 text-red-600"><X className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        <input ref={up.ref} type="file" accept="image/*" multiple className="hidden" onChange={(e) => up.run([...e.target.files].slice(0, max - value.length))} data-testid={`${testid}-file`} />
        <button type="button" disabled={up.busy || value.length >= max} onClick={() => up.ref.current.click()} data-testid={`${testid}-upload`}
          className="grid h-24 w-32 place-items-center rounded-xl border border-dashed border-ink/20 text-xs text-ink/60 hover:border-brand">
          {up.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="flex flex-col items-center gap-1"><ImagePlus className="h-4 w-4" />Add photos</span>}
        </button>
      </div>
    </div>
  );
};

export const Items = ({ value = [], onChange, blank, render, testid, addLabel }) => (
  <div className="space-y-4" data-testid={testid}>
    {value.map((it, i) => (
      <div key={i} className="rounded-xl border border-ink/10 p-4" data-testid={`${testid}-item-${i}`}>
        <div className="mb-3 flex justify-end gap-2">
          {i > 0 && <button type="button" className="btn-ghost !px-2" aria-label="Move up" onClick={() => onChange(move(value, i, -1))}><ArrowUp className="h-4 w-4" /></button>}
          {i < value.length - 1 && <button type="button" className="btn-ghost !px-2" aria-label="Move down" onClick={() => onChange(move(value, i, 1))}><ArrowDown className="h-4 w-4" /></button>}
          <button type="button" className="btn-ghost !px-2 !text-red-600" aria-label="Remove" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
        <div className="grid gap-4 md:grid-cols-2">{render(it, (p) => onChange(value.map((x, j) => (j === i ? { ...x, ...p } : x))), i)}</div>
      </div>
    ))}
    <button type="button" className="btn-outline !py-2" onClick={() => onChange([...value, blank])} data-testid={`${testid}-add`}><Plus className="h-4 w-4" />{addLabel}</button>
  </div>
);
